import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * SettingsContext — persists ONLY the Settings active sub-tab across navigation
 * AND F5 refresh. Lives above the router so it never unmounts when switching
 * pages.
 *
 * Scope: just the active tab. Nothing else (no general/notification/integration
 * form fields, no sub-account search, no dialogs) is intentionally persisted,
 * per product decision.
 * URL `?tab=` still wins on the initial render.
 *
 * Pattern: Direct Consume (same as MediaContext / AdminPanelContext).
 */
const SettingsContext = createContext(null);

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
};

const STORAGE_KEY = 'st_state';

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

export const SettingsProvider = ({ children }) => {
  const saved = loadPersistedState();

  const [persistedActiveTab, setPersistedActiveTab] = useState(saved?.activeTab || null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ activeTab: persistedActiveTab }));
    } catch { /* ignore */ }
  }, [persistedActiveTab]);

  return (
    <SettingsContext.Provider value={{ persistedActiveTab, setPersistedActiveTab }}>
      {children}
    </SettingsContext.Provider>
  );
};
